// src/components/features/ParkingSessions/ParkingPhotoSection.tsx
//
// Shared photo section for parking session drawers.
//
// States:
//   - No photo: full-width "Add Photo" button
//   - Has photo: thumbnail card with tap-to-preview, replace, remove
//
// Used by: StartParkingDrawer, ParkingSessionEditDrawer

import React from "react";
import {
  Stack,
  Group,
  Text,
  Box,
  Image,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon, AppButton } from "@/components/generic";

// ============================================
// Types
// ============================================

export interface PhotoDisplayData {
  fileUrl: string | null;
  thumbnailUrl: string | null;
  fileName: string | null;
}

export const EMPTY_PHOTO_DISPLAY: PhotoDisplayData = {
  fileUrl: null,
  thumbnailUrl: null,
  fileName: null,
};

export interface ParkingPhotoSectionProps {
  /** Whether a photo is attached */
  hasPhoto: boolean;

  /** Display URLs for the attached photo */
  photoDisplay: PhotoDisplayData;

  /** Open the capture flow */
  onOpenCapture: () => void;

  /** Remove the current photo */
  onRemovePhoto: () => void;

  /** Open full-screen preview */
  onPreviewPhoto: () => void;

  /** Whether actions are disabled (e.g. during submit) */
  disabled?: boolean;
}

// ============================================
// Constants
// ============================================

/** Thumbnail height in pixels */
const PHOTO_THUMB_HEIGHT = 140;

// ============================================
// Component
// ============================================

const ParkingPhotoSectionComponent: React.FC<ParkingPhotoSectionProps> = ({
  hasPhoto,
  photoDisplay,
  onOpenCapture,
  onRemovePhoto,
  onPreviewPhoto,
  disabled = false,
}) => {
  const { t } = useTranslation(["parkingSessions", "common"]);

  if (!hasPhoto) {
    return (
      <AppButton
        variant="secondary"
        size="md"
        iconName="Camera"
        onClick={onOpenCapture}
        disabled={disabled}
        fullWidth
      >
        {t("parkingSessions:form.photo.addButton", {
          defaultValue: "Add Photo",
        })}
      </AppButton>
    );
  }

  const thumbSrc = photoDisplay.thumbnailUrl || photoDisplay.fileUrl;

  return (
    <Box
      style={{
        position: "relative",
        borderRadius: "var(--mantine-radius-md)",
        overflow: "hidden",
        border: "1px solid var(--mantine-color-neutral-2)",
        backgroundColor: "var(--mantine-color-neutral-0)",
      }}
    >
      {/* Thumbnail — tappable for full-screen preview */}
      <Box
        onClick={onPreviewPhoto}
        style={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: PHOTO_THUMB_HEIGHT,
          overflow: "hidden",
        }}
      >
        {thumbSrc ? (
          <Image
            src={thumbSrc}
            alt={
              photoDisplay.fileName ||
              t("parkingSessions:form.photo.alt", {
                defaultValue: "Parking photo",
              })
            }
            fit="cover"
            h={PHOTO_THUMB_HEIGHT}
            w="100%"
            style={{ display: "block" }}
          />
        ) : (
          <Stack align="center" gap={4} py="md">
            <AppIcon
              iconName="Photo"
              size={32}
              color="var(--mantine-color-neutral-4)"
            />
            <Text size="xs" c="dimmed">
              {t("parkingSessions:form.photo.loadingPreview", {
                defaultValue: "Loading preview...",
              })}
            </Text>
          </Stack>
        )}
      </Box>

      {/* Action buttons — top-right corner */}
      <Group
        gap={4}
        style={{
          position: "absolute",
          top: 8,
          right: 8,
        }}
      >
        {/* Replace */}
        <Tooltip
          label={t("parkingSessions:form.photo.replaceTooltip", {
            defaultValue: "Replace photo",
          })}
          position="bottom"
          withArrow
        >
          <ActionIcon
            variant="filled"
            color="dark"
            size="md"
            radius="xl"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onOpenCapture();
            }}
            disabled={disabled}
            style={{ opacity: 0.85 }}
            aria-label={t("parkingSessions:form.photo.replaceTooltip", {
              defaultValue: "Replace photo",
            })}
          >
            <AppIcon iconName="CameraRotate" size={16} />
          </ActionIcon>
        </Tooltip>

        {/* Remove */}
        <Tooltip
          label={t("parkingSessions:form.photo.removeTooltip", {
            defaultValue: "Remove photo",
          })}
          position="bottom"
          withArrow
        >
          <ActionIcon
            variant="filled"
            color="red"
            size="md"
            radius="xl"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onRemovePhoto();
            }}
            disabled={disabled}
            style={{ opacity: 0.85 }}
            aria-label={t("parkingSessions:form.photo.removeTooltip", {
              defaultValue: "Remove photo",
            })}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {/* Tap hint */}
      <Box
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "6px 12px",
          background:
            "linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 100%)",
        }}
      >
        <Text size="xs" c="white" ta="center">
          {t("parkingSessions:form.photo.tapToPreview", {
            defaultValue: "Tap to preview",
          })}
        </Text>
      </Box>
    </Box>
  );
};

export const ParkingPhotoSection = React.memo(ParkingPhotoSectionComponent);